import { Certificate, Input } from '@components';
import { routes } from '@constants';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigateWithOfflineMode, useScreenSize } from '@hooks';
import { Button, Flex, theme, Typography } from '@styles';
import { schemas } from '@utils';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  getDataFromLS,
  setDataToLS,
} from 'src/components/CookiesModal/CookiesModal.propTypes';

const CertificateSettings = () => {
  const { i18n } = useTranslation();
  const { state } = useLocation();
  const { isTabletLG } = useScreenSize();
  const [existingData, setExistingData] = useState({
    name: null,
  });
  const [languageOfCertificate, setLanguageOfCertificate] = useState(
    getDataFromLS('certificateLanguage')
  );
  const { navigate } = useNavigateWithOfflineMode();
  const { clearErrors, control, formState, handleSubmit } = useForm({
    defaultValues: {
      name: getDataFromLS('certificateName') || '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schemas.nameForm),
  });

  useEffect(() => {
    setExistingData({
      name: getDataFromLS('certificateName'),
    });
  }, []);

  const onClick = () => {
    clearErrors();
    handleSubmit(async (formData) => {
      setDataToLS('certificateName', formData.name);
      setDataToLS('certificateLanguage', languageOfCertificate);
      setExistingData({
        name: formData.name,
      });
    })();
    navigate(routes.profile);
  };

  const titleByLevel = {
    'Officer Level': 'OfficerLevel',
    'Soldier Level': 'SoldierLevel',
  };

  const title = titleByLevel[state?.title] || state?.title;

  return (
    <Flex flexDirection="column" my={90}>
      <FormProvider control={control} formState={formState}>
        <Flex
          flexDirection="column"
          justifyContent="center"
          mx="auto"
          py={50}
          width="fit-content"
        >
          <Flex
            background={theme.colors.hunterGreen[90]}
            border="1px solid #4A4A4A"
            justifyContent="center"
            mx={isTabletLG ? '16px' : 'auto'}
            px={isTabletLG ? 16 : 100}
            py={80}
          >
            <Flex flexDirection="column">
              <Flex flexDirection="column" p={15}>
                <Typography
                  color={theme.colors.white[100]}
                  textAlign="center"
                  variant="BN-regular-s92-lh78"
                >
                  {i18n.t('Certificate.Congratulations')}
                </Typography>
              </Flex>
              <Typography
                color={theme.colors.white[100]}
                textAlign="center"
                variant="MS-regular-s26-lh40"
              >
                {i18n.t('Certificate.CongratulationsOn')}
              </Typography>
              <Typography
                color={theme.colors.schoolBusYellow[100]}
                textAlign="center"
                variant="MS-bold-s26-lh40"
              >
                {`${i18n.t(`Certificate.${title}`)} ${i18n.t('Certificate.OfPlatformCourse')}`}
              </Typography>
              <Typography
                color={theme.colors.white[100]}
                mb={50}
                textAlign="center"
                variant="MS-regular-s26-lh40"
              >
                {`${i18n.t('Certificate.On')} ${state?.date || ''}`}
              </Typography>
              <Flex flexDirection="column" my={24}>
                <Typography
                  color={theme.colors.white[100]}
                  mb={24}
                  variant="MS-regular-s24-lh37"
                >
                  {i18n.t('Certificate.ChooseLanguage')}
                </Typography>
                <Flex>
                  <Button
                    height="45px"
                    mr={16}
                    onClick={() => setLanguageOfCertificate('en')}
                    variant={
                      languageOfCertificate === 'en' ? 'primary' : 'secondary'
                    }
                    width="145px"
                  >
                    <Typography
                      color={
                        languageOfCertificate === 'en'
                          ? theme.colors.black[100]
                          : theme.colors.white[100]
                      }
                      variant="MS-bold-s16-lh25"
                    >
                      {i18n.t('Certificate.English').toUpperCase()}
                    </Typography>
                  </Button>
                  <Button
                    height="45px"
                    onClick={() => setLanguageOfCertificate('ua')}
                    py={10}
                    variant={
                      languageOfCertificate === 'ua' ? 'primary' : 'secondary'
                    }
                    width="145px"
                  >
                    <Typography
                      color={
                        languageOfCertificate === 'ua'
                          ? theme.colors.black[100]
                          : theme.colors.white[100]
                      }
                      variant="MS-bold-s16-lh25"
                    >
                      {i18n.t('Certificate.Ukrainian').toUpperCase()}
                    </Typography>
                  </Button>
                </Flex>
              </Flex>
              <Flex flexDirection="column">
                <Typography
                  color={theme.colors.white[100]}
                  mb="8px"
                  variant="MS-regular-s24-lh37"
                >
                  {i18n.t('Certificate.EnterName')}
                </Typography>
                <Typography
                  color={theme.colors.gray}
                  mb={24}
                  variant="MS-regular-s16-lh25"
                >
                  {i18n.t('Certificate.WeDontRetain')}
                </Typography>
                <Input
                  mb={48}
                  name="name"
                  type="text"
                  variant="fullNameOrCallSign"
                  withoutIcon
                  {...(isTabletLG
                    ? { marginBottom: '48px' }
                    : { marginRight: '16px' })}
                />
              </Flex>
              <Certificate
                date={state?.date}
                isTablet={isTabletLG}
                language={languageOfCertificate}
                name={existingData.name}
                size="medium"
                title={title}
              />
            </Flex>
          </Flex>
          <Flex
            alignItems="center"
            gap={32}
            justifyContent="end"
            mt={32}
            mx={isTabletLG ? '16px' : 'auto'}
          >
            <Typography color={theme.colors.gray} variant="MS-regular-s16-lh25">
              {i18n.t('Certificate.FindInProfile')}
            </Typography>
            <Button
              onClick={onClick}
              type="submit"
              variant="primary"
              width={isTabletLG ? '100%' : '394px'}
            >
              <Typography variant="MS-semibold-s16-lh25">
                {i18n.t('ProfilePage.Continue')}
              </Typography>
            </Button>
          </Flex>
        </Flex>
      </FormProvider>
    </Flex>
  );
};

export default CertificateSettings;
